import { useEffect } from "react";

import type { RefObject } from "react";

function swallow(event: Event) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Opens the menu behind `buttonRef` when Shift+F10 or the Menu key is pressed with focus inside
 * `containerRef`: the keyboard counterpart of right-clicking it. Keys from a treeitem nested in the
 * container are left alone, since that row is the one they act on.
 */
export function useContextMenuKey(
  containerRef: RefObject<HTMLElement | null>,
  buttonRef: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const doc = container.ownerDocument;

    // Windows follows Shift+F10 / Menu key with a native contextmenu event, which would open the
    // right-click menu on top of the one opened here. It comes before any further input.
    const disarm = () => {
      doc.removeEventListener("contextmenu", swallow, true);
      doc.removeEventListener("keydown", disarm, true);
      doc.removeEventListener("pointerdown", disarm, true);
    };
    const arm = () => {
      doc.addEventListener("contextmenu", swallow, { capture: true, once: true });
      doc.addEventListener("keydown", disarm, true);
      doc.addEventListener("pointerdown", disarm, true);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const isMenuKey = event.key === "ContextMenu" || (event.shiftKey && event.key === "F10");
      const button = buttonRef.current;
      if (!isMenuKey || !button || event.defaultPrevented) return;
      const target = event.target as Element;
      if (target.closest("input, textarea")) return;
      const row = target.closest('[role="treeitem"]');
      if (row !== container && container.contains(row)) return;

      event.preventDefault();
      button.focus();
      // Headless UI opens on Enter whatever pointer was used last, unlike its click handler
      button.dispatchEvent(new window.KeyboardEvent("keydown", { key: "Enter", bubbles: true }));
      arm();
    };

    container.addEventListener("keydown", onKeyDown);
    return () => {
      container.removeEventListener("keydown", onKeyDown);
      disarm();
    };
  }, [containerRef, buttonRef]);
}
